import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import {
    createLegalAwarenessProsecutorAssignment,
    deleteLegalAwarenessProsecutorAssignment,
    updateLegalAwarenessProsecutorAssignment,
    getSingleLegalAwareness,
} from "services/legalAwarenessService";
import { useTranslation } from "react-i18next";

const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
};

export default function EditTeamModal({
    request,
    isOpen,
    onClose,
    setFormState,
}: {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setFormState: any;
}) {
    const { t } = useTranslation();
    const contextData = useAuth();
    const { showToast } = useToast();
    const [employees, setEmployees] = useState<any[]>([]);
    const [teams, setTeams] = useState<any[]>(request?.teams || []);
    const [selectedId, setSelectedId] = useState<string>("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getEmployeesByAdminAndOrg({
            bearer:
                contextData.token ??
                contextData?.user?.access_token ??
                contextData?.user?.user?.access_token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
        })
            .then((data) => {
                setEmployees(
                    data
                        .filter((emp: any) => emp.roles?.some((role: string) => role === "LegalResearchProsecutor"))
                        .map((emp: any) => ({
                            id: emp.user.id,
                            name: emp.user.first_name,
                            role: emp.roles[0] || "LegalResearchProsecutor",
                        }))
                );
            })
            .catch((error) => {
                console.error("Error fetching employees:", error);
                showToast(t("FailedToFetchEmployees") || "Failed to fetch employees.", "error");
            });
    }, [contextData, showToast, t]);

    const refresh = async () => {
        try {
            const updated = await getSingleLegalAwareness(request.id);
            setTeams(updated?.teams || []);
            setFormState((prev: any) => ({ ...prev, ...updated }));
        } catch (error) {
            console.error("Error refreshing team:", error);
        }
    };

    const handleAdd = async () => {
        if (!selectedId) return;
        const employee = employees.find((e) => String(e.id) === selectedId);
        if (!employee) return;
        if (teams.some((m: any) => String(m.prosecutor_id ?? m.id) === String(employee.id))) {
            showToast(t("ProsecutorAlreadyAdded") || "This prosecutor is already added to the team.", "error");
            return;
        }
        setLoading(true);
        try {
            const data = new FormData();
            data.append("awareness", request.id.toString());
            data.append("prosecutor_name", employee.name);
            data.append("prosecutor_id", employee.id.toString());
            data.append("role", teams.length === 0 ? "leader" : "member");
            await createLegalAwarenessProsecutorAssignment(data);
            setSelectedId("");
            await refresh();
            showToast(t("ProsecutorAddedToTeam") || "Prosecutor added to team.", "success");
        } catch (error) {
            console.error("Error adding prosecutor:", error);
            showToast(t("FailedToUpdateTeam") || "Failed to update team.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (assignmentId: string | number) => {
        setLoading(true);
        try {
            await deleteLegalAwarenessProsecutorAssignment(assignmentId);
            await refresh();
            showToast(t("ProsecutorRemovedFromTeam") || "Prosecutor removed from team.", "success");
        } catch (error) {
            console.error("Error removing prosecutor:", error);
            showToast(t("FailedToUpdateTeam") || "Failed to update team.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleLeader = async (member: any) => {
        const isLeader = member?.role === "leader" || member?.is_team_leader;
        setLoading(true);
        try {
            const data = new FormData();
            data.append("role", isLeader ? "member" : "leader");
            data.append("is_team_leader", isLeader ? "false" : "true");
            await updateLegalAwarenessProsecutorAssignment(member.id, data);
            await refresh();
        } catch (error) {
            console.error("Error updating team leader:", error);
            showToast(t("FailedToUpdateTeam") || "Failed to update team.", "error");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-6 py-4 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{t("OAG") || "OAG"}</p>
                        <h3 className="text-white font-semibold text-lg">{t("EditTeam") || "Edit Team"}</h3>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto">
                    {/* Add prosecutor */}
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("AddTeamMember") || "Add Team Member"}
                    </label>
                    <div className="flex gap-2 mb-5">
                        <select
                            className="flex-1 p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#0A1D39]"
                            value={selectedId}
                            onChange={(e) => setSelectedId(e.target.value)}
                        >
                            <option value="">{t("SelectProsecutor") || "Select Prosecutor"}</option>
                            {employees.map((emp) => (
                                <option key={emp.id} value={emp.id}>{emp.name} - {emp.role}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={handleAdd}
                            disabled={loading || !selectedId}
                            className="px-4 py-2 rounded-md bg-[#0A1D39] text-white text-sm font-semibold hover:bg-[#13294d] disabled:opacity-50"
                        >
                            {t("Add") || "Add"}
                        </button>
                    </div>

                    {/* Current team */}
                    <p className="text-sm font-semibold text-gray-700 mb-2">{t("TeamMembers") || "Team Members"}</p>
                    <div className="space-y-3">
                        {teams.length === 0 && (
                            <p className="text-gray-500 text-sm italic">{t("NoTeamMembersAddedYet") || "No team members added yet."}</p>
                        )}
                        {teams.map((member: any) => {
                            const name = member.name || member.prosecutor_name || "";
                            const isLeader = member?.role === "leader" || member?.is_team_leader;
                            return (
                                <div key={member.id} className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
                                    <div className="w-10 h-10 rounded-full mr-3 bg-[#0A1D39] text-white flex items-center justify-center font-bold text-sm select-none">
                                        {getInitials(name)}
                                    </div>
                                    <div className="flex-grow">
                                        <p className="font-semibold text-gray-800">{name}</p>
                                        <p className="text-sm text-gray-500">{isLeader ? (t("TeamLeader") || "Team Leader") : (t("Member") || "Member")}</p>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isLeader}
                                                onChange={() => handleToggleLeader(member)}
                                                disabled={loading}
                                                className="accent-[#0A1D39] h-4 w-4"
                                            />
                                            {t("IsTeamLeader") || "is Team Leader"}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => handleRemove(member.id)}
                                            disabled={loading}
                                            className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                                        >
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="#9B0505" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold hover:bg-[#13294d]"
                    >
                        {t("Done") || "Done"}
                    </button>
                </div>
            </div>
        </div>
    );
}
